import axios from '../axiosInstance';
import { getApiBaseUrl, getForwardedHost } from './apiClient';

/**
 * CANsense Notification Channel API Client
 * Handles notification channel CRUD operations and testing
 */

// ============================================================================
// Types
// ============================================================================

export type NotificationChannelType =
  | 'canmail'
  | 'email'
  | 'slack'
  | 'pagerduty'
  | 'teams'
  | 'webhook';

export type AlertSeverity = 'critical' | 'warning' | 'info';

export interface NotificationChannel {
  id: number;
  type: NotificationChannelType;
  name: string;
  value: string;
  config?: Record<string, unknown>;
  member_count?: number;
  watch_all: boolean;
  watch_exporter_ids: number[];
  watch_website_ids: number[];
  severities: AlertSeverity[];
  verified: boolean;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateNotificationChannelData {
  type: NotificationChannelType;
  name: string;
  value: string;
  config?: Record<string, unknown>;
  watch_all?: boolean;
  watch_exporter_ids?: number[];
  watch_website_ids?: number[];
  severities?: AlertSeverity[];
}

export interface NotificationChannelTestResult {
  success: boolean;
  message: string;
  deliveryTime?: number;
  error?: string;
}

export interface CanmailList {
  id: number;
  name: string;
  memberCount: number;
}

export interface ListMember {
  contactId: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ContactSearchResult {
  contactId: number;
  firstName: string;
  lastName: string;
  email: string;
}

// ============================================================================
// Helpers
// ============================================================================

const getApiUrl = (endpoint: string): string => {
  return `${getApiBaseUrl()}/sense/notification-channels${endpoint}`;
};

// ============================================================================
// Notification Channel Service
// ============================================================================

export class NotificationChannelService {
  async getChannels(): Promise<NotificationChannel[]> {
    const API_URL = getApiUrl('');
    const response = await axios.get(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    const unwrapped = response.data?.data || response.data;
    const rawChannels = unwrapped?.channels || unwrapped || [];
    return rawChannels.map((c: Record<string, unknown>) => this.transformChannelResponse(c));
  }

  async getChannel(id: number): Promise<NotificationChannel> {
    const API_URL = getApiUrl(`/${id}`);
    const response = await axios.get(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    const raw = response.data?.data || response.data;
    return this.transformChannelResponse(raw);
  }

  async createChannel(channelData: CreateNotificationChannelData): Promise<NotificationChannel> {
    const API_URL = getApiUrl('');
    const apiPayload = this.transformChannelPayload(channelData);
    const response = await axios.post(API_URL, apiPayload, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  async updateChannel(
    id: number,
    channelData: Partial<CreateNotificationChannelData>
  ): Promise<NotificationChannel> {
    const API_URL = getApiUrl(`/${id}`);
    const apiPayload = this.transformChannelPayload(channelData as CreateNotificationChannelData);
    const response = await axios.put(API_URL, apiPayload, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  /**
   * Transform backend channel response to frontend format.
   * Maps camelCase backend fields to the frontend NotificationChannel interface.
   */
  private transformChannelResponse(c: Record<string, unknown>): NotificationChannel {
    // Map channelType -> type, converting canmail_list -> canmail
    const rawType = (c.channelType || c.channel_type || c.type || '') as string;
    const type = (rawType === 'canmail_list' ? 'canmail' : rawType) as NotificationChannelType;

    // Compose the "value" field from the type-specific field
    let value = (c.value as string) || '';
    if (!value) {
      switch (rawType) {
        case 'email':
          value = (c.email as string) || '';
          break;
        case 'canmail_list':
          value = c.canmailListId != null ? String(c.canmailListId) : '';
          break;
        case 'slack':
        case 'teams':
        case 'webhook':
          value = (c.webhookUrl as string) || '';
          break;
        case 'pagerduty':
          value = (c.pagerdutyRoutingKey as string) || '';
          break;
      }
    }

    // Map watchedExporters array of objects to array of IDs
    const watchedExporters = c.watchedExporters as Array<{ id: number }> | null;
    const watchExporterIds = watchedExporters
      ? watchedExporters.map((e) => e.id)
      : (c.watch_exporter_ids as number[]) || [];

    return {
      id: c.id as number,
      type,
      name: (c.name as string) || '',
      value,
      member_count: (c.memberCount ?? c.member_count ?? 0) as number,
      watch_all: !!(c.watchAllExporters ?? c.watch_all ?? false),
      watch_exporter_ids: watchExporterIds,
      watch_website_ids: (c.watch_website_ids as number[]) || [],
      severities: (c.severityFilter ?? c.severities ?? ['critical', 'warning']) as AlertSeverity[],
      verified: !!(c.isVerified ?? c.verified ?? false),
      enabled: !!(c.isEnabled ?? c.enabled ?? true),
      created_at: (c.createdAt ?? c.created_at ?? '') as string,
      updated_at: (c.updatedAt ?? c.updated_at ?? '') as string,
    };
  }

  /**
   * Transform frontend channel data to backend API format
   */
  private transformChannelPayload(channelData: CreateNotificationChannelData): Record<string, unknown> {
    const channelType = channelData.type === 'canmail' ? 'canmail_list' : channelData.type;

    const apiPayload: Record<string, unknown> = {
      name: channelData.name,
      channelType,
      watchAllExporters: channelData.watch_all ?? false,
      exporterIds: channelData.watch_exporter_ids ?? [],
      severityFilter: channelData.severities ?? ['critical', 'warning'],
    };

    switch (channelType) {
      case 'email':
        apiPayload.email = channelData.value;
        break;
      case 'canmail_list':
        apiPayload.canmailListId = parseInt(channelData.value, 10);
        break;
      case 'slack':
        apiPayload.webhookUrl = channelData.value;
        if (channelData.config?.slackChannel) {
          apiPayload.webhookChannel = channelData.config.slackChannel;
        }
        break;
      case 'teams':
      case 'webhook':
        apiPayload.webhookUrl = channelData.value;
        break;
      case 'pagerduty':
        apiPayload.pagerdutyRoutingKey = channelData.value;
        break;
    }

    return apiPayload;
  }

  async deleteChannel(id: number): Promise<void> {
    const API_URL = getApiUrl(`/${id}`);
    await axios.delete(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
  }

  async testChannel(id: number): Promise<NotificationChannelTestResult> {
    const API_URL = getApiUrl(`/${id}/verify`);
    const response = await axios.post(API_URL, {}, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  async verifyChannel(id: number): Promise<NotificationChannel> {
    const API_URL = getApiUrl(`/${id}/verify`);
    const response = await axios.post(
      API_URL,
      {},
      {
        headers: {
          'X-Forwarded-Host': getForwardedHost(API_URL),
        },
      }
    );
    return response.data?.data || response.data;
  }

  async toggleChannel(id: number, enabled: boolean): Promise<NotificationChannel> {
    const API_URL = getApiUrl(`/${id}/toggle`);
    const response = await axios.patch(
      API_URL,
      { enabled },
      {
        headers: {
          'X-Forwarded-Host': getForwardedHost(API_URL),
        },
      }
    );
    return response.data?.data || response.data;
  }

  async getCanmailLists(search?: string): Promise<CanmailList[]> {
    const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
    const API_URL = getApiUrl(`/canmail-lists${searchParam}`);
    const response = await axios.get(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    const unwrapped = response.data?.data || response.data;
    const lists = unwrapped?.lists || unwrapped || [];
    return lists.map((list: { listId: number; name: string; memberCount: number }) => ({
      id: list.listId,
      name: list.name,
      memberCount: list.memberCount,
    }));
  }

  async getListMembers(listId: number): Promise<{ listName: string; members: ListMember[]; total: number }> {
    const API_URL = getApiUrl(`/canmail-lists/${listId}/members`);
    const response = await axios.get(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    const data = response.data?.data || response.data;
    return {
      listName: data.listName,
      members: data.members || [],
      total: data.total || 0,
    };
  }

  async addListMember(listId: number, contactId: number): Promise<void> {
    const API_URL = getApiUrl(`/canmail-lists/${listId}/members`);
    await axios.post(
      API_URL,
      { contactId },
      {
        headers: {
          'X-Forwarded-Host': getForwardedHost(API_URL),
        },
      }
    );
  }

  async removeListMember(listId: number, contactId: number): Promise<void> {
    const API_URL = getApiUrl(`/canmail-lists/${listId}/members/${contactId}`);
    await axios.delete(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
  }

  async searchContacts(query: string): Promise<ContactSearchResult[]> {
    const API_URL = getApiUrl(`/contacts/search?q=${encodeURIComponent(query)}`);
    const response = await axios.get(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    const data = response.data?.data || response.data;
    return data.contacts || [];
  }
}

// Export singleton instance
export const notificationChannelService = new NotificationChannelService();

// Export with original name for backward compatibility
export const notificationChannelApi = notificationChannelService;
